"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.sliceKey = exports.reducer = exports.betsHistoryProviderActions = exports.initialState = void 0;
const toolkit_1 = require("@reduxjs/toolkit");
const betweb_openapi_axios_1 = require("betweb-openapi-axios");
const dayjs_1 = __importDefault(require("dayjs"));
const uniq_1 = __importDefault(require("lodash/uniq"));
const api_1 = require("../../utils/api");
const _reduxjs_1 = require("../../utils/@reduxjs");
const format_1 = require("../../utils/format");
const types_1 = require("../../utils/dates/types");
const dates_1 = require("../../utils/dates");
const types_2 = require("../ConfigProvider/types");
const types_3 = require("./types");
const constants_1 = require("./constants");
const REDUCER_KEY = 'betsHistoryProvider';
// The initial state of the BetsHistoryProvider container
exports.initialState = (0, _reduxjs_1.getPreloadedState)(REDUCER_KEY, {
    data: {
        bets: {
            fetchItems: api_1.requestInitialState,
            sortedIds: [],
            hasMore: false,
        },
        betsStatistics: {
            fetchItem: api_1.requestInitialState,
        },
    },
    bets: {
        entries: {},
        fetchItems: api_1.requestInitialState,
        deleteItems: api_1.requestInitialState,
    },
    filters: {
        dates: { startDate: null, endDate: null },
        datePickerRange: {
            selectDatePeriod: types_1.EDatePeriods.Default,
            inputDates: { startDate: '', endDate: '' },
        },
        betsStatus: betweb_openapi_axios_1.BetStatus.Current,
        gameVendor: betweb_openapi_axios_1.VirtualGameVendor.Sportsbook,
        betsTypes: null,
    },
    layout: {
        collapsedBetsData: {
            collapsedList: [],
            isCollapsedAll: false,
        },
        isDisplayAllBetsStatistics: false,
        selectedDatePeriod: null,
        mainTabBetStatus: types_3.EMainTabBetStatus.Current,
    },
    pagination: {
        limit: 15,
        offset: 0,
    },
    clientType: types_2.EClientTypes.DEFAULT,
    cashout: {
        data: {
            entries: {},
            currentCashout: null,
            soldBet: null,
        },
        fetchItems: api_1.requestInitialState,
        layout: {
            statusView: types_3.ECashoutStatusView.NONE,
        },
        sse: {
            channel: null,
        },
    },
});
const betsHistoryProviderSlice = (0, toolkit_1.createSlice)({
    name: REDUCER_KEY,
    initialState: exports.initialState,
    reducers: {
        changeClientType(state, { payload }) {
            state.clientType = payload;
        },
        resetBetsHistory(state) {
            // Reset Bets History data
            state.filters = exports.initialState.filters;
            state.layout = exports.initialState.layout;
            clearBetHistoryPagination(state);
            // Reset Bets Cashout data
            state.cashout.data.entries = {};
            state.cashout.data.currentCashout = null;
            state.cashout.fetchItems.error = null;
        },
        fetchBets(state, _action) {
            state.bets.fetchItems.loading = true;
            state.bets.fetchItems.error = null;
        },
        fetchBetsSuccess(state, { payload }) {
            state.bets.entries = {
                ...state.bets.entries,
                ...(0, _reduxjs_1.toIdsMap)(payload.resData.items),
            };
            state.bets.fetchItems.loading = false;
            state.bets.fetchItems.error = null;
        },
        fetchBetsError(state, { payload }) {
            state.bets.fetchItems.loading = false;
            state.bets.fetchItems.error = payload;
        },
        deleteBets(state) {
            state.bets.deleteItems.loading = true;
            state.bets.deleteItems.error = null;
        },
        deleteBetsSuccess(state) {
            clearBetHistoryAfterDeleteBetsSuccess(state);
        },
        deleteBetsError(state, { payload }) {
            state.bets.deleteItems.loading = false;
            state.bets.deleteItems.error = payload;
        },
        setFilterGameVendor(state, { payload }) {
            state.filters.gameVendor = payload;
            clearBetHistoryPagination(state);
        },
        setFilterBetStatus(state, { payload }) {
            state.filters.betsStatus = payload;
            clearBetHistoryPagination(state);
        },
        setFilterBetsTypes(state, { payload }) {
            state.filters.betsTypes = payload;
            clearBetHistoryPagination(state);
        },
        setCollapsedBets(state, { payload: id }) {
            const { collapsedList } = state.layout.collapsedBetsData;
            state.layout.collapsedBetsData.collapsedList = collapsedList.includes(id)
                ? [...collapsedList.filter((item) => item !== id)]
                : [...collapsedList, id];
        },
        toggleCollapseAllBets(state) {
            const newCollapseStatus = !state.layout.collapsedBetsData.isCollapsedAll;
            state.layout.collapsedBetsData.isCollapsedAll = newCollapseStatus;
            state.layout.collapsedBetsData.collapsedList = newCollapseStatus
                ? state.data.bets.sortedIds
                : [];
        },
        fetchHistoryBetsPage(state, _action) {
            state.data.bets.fetchItems.loading = true;
            state.data.bets.fetchItems.error = null;
        },
        refreshHistoryBetsPage(state, _action) {
            state.data.bets.fetchItems.refreshLoading = true;
            state.pagination = exports.initialState.pagination;
            state.data.bets.fetchItems.error = null;
        },
        fetchHistoryBetsPageSuccess(state, { payload }) {
            state.data.bets.fetchItems.loading = false;
            state.data.bets.fetchItems.refreshLoading = false;
            state.data.bets.fetchItems.error = null;
            state.data.bets.hasMore = payload.resData.hasMore;
            if (state.clientType === types_2.EClientTypes.WEB_MOBILE) {
                // To persist collapsed bets layout state
                if (state.layout.collapsedBetsData.isCollapsedAll) {
                    state.layout.collapsedBetsData.collapsedList = (0, uniq_1.default)([
                        ...state.data.bets.sortedIds,
                        ...(0, _reduxjs_1.toIdsArray)(payload.resData.items),
                    ]);
                }
                state.data.bets.sortedIds = (0, uniq_1.default)([
                    ...state.data.bets.sortedIds,
                    ...(0, _reduxjs_1.toIdsArray)(payload.resData.items),
                ]);
            }
            else {
                // To persist collapsed bets layout state
                if (state.layout.collapsedBetsData.isCollapsedAll) {
                    state.layout.collapsedBetsData.collapsedList = (0, uniq_1.default)([
                        ...state.data.bets.sortedIds,
                        ...(0, _reduxjs_1.toIdsArray)(payload.resData.items),
                    ]);
                }
                state.data.bets.sortedIds = (0, uniq_1.default)([
                    ...state.data.bets.sortedIds,
                    ...(0, _reduxjs_1.toIdsArray)(payload.resData.items),
                ]);
            }
        },
        fetchHistoryBetsPageError(state, { payload }) {
            state.data.bets.fetchItems.loading = false;
            state.data.bets.fetchItems.refreshLoading = false;
            state.data.bets.fetchItems.error = payload;
            clearBetHistoryPagination(state);
        },
        setPagination(state, { payload }) {
            state.pagination = { ...state.pagination, ...payload };
        },
        setDatePickerRangeValues(state, { payload }) {
            const [startDate, endDate] = payload;
            state.layout.selectedDatePeriod = null; // To reset previously selected date period
            state.filters.dates.startDate = startDate;
            state.filters.dates.endDate = endDate;
            clearBetHistoryPagination(state);
        },
        setDatePeriod(state, { payload }) {
            if (state.layout.selectedDatePeriod === payload) {
                // To reset the current date period, if it was already selected
                state.layout.selectedDatePeriod = null;
                state.filters.dates.startDate = null;
                state.filters.dates.endDate = null;
            }
            else {
                // Select the provided date period otherwise
                state.layout.selectedDatePeriod = payload;
                const [startDate, endDate] = dates_1.DATE_PERIOD_BY_TITLE[payload];
                state.filters.dates.startDate = startDate;
                state.filters.dates.endDate = endDate;
            }
            clearBetHistoryPagination(state);
        },
        fetchBetsStatistics(state) {
            state.data.betsStatistics.fetchItem.loading = true;
            state.data.betsStatistics.fetchItem.error = null;
        },
        fetchBetsStatisticsSuccess(state, { payload }) {
            state.data.betsStatistics.fetchItem.loading = false;
            state.data.betsStatistics.fetchItem.error = null;
            state.data.betsStatistics.statisticsList = payload;
        },
        fetchBetsStatisticsError(state, { payload }) {
            state.data.betsStatistics.fetchItem.loading = false;
            state.data.betsStatistics.fetchItem.error = payload;
        },
        toggleDisplayAllBetsStatistics(state) {
            state.layout.isDisplayAllBetsStatistics =
                !state.layout.isDisplayAllBetsStatistics;
        },
        setMainTabBetStatus(state, { payload }) {
            state.layout.mainTabBetStatus = payload;
            if (payload === types_3.EMainTabBetStatus.Current) {
                state.filters.betsStatus = betweb_openapi_axios_1.BetStatus.Current;
            }
            else {
                state.filters.betsStatus = betweb_openapi_axios_1.BetStatus.Computed;
            }
            clearBetHistoryPagination(state);
        },
        setDatePickerRangeInputValue(state, { payload }) {
            state.layout.selectedDatePeriod = null; // To reset previously selected date period
            state.filters.datePickerRange.inputDates = {
                ...state.filters.datePickerRange.inputDates,
                ...payload,
            };
        },
        setDatePickerSelectDatePeriod(state, { payload }) {
            state.filters.datePickerRange.selectDatePeriod = payload;
        },
        resetSelectDatePeriod(state) {
            clearSelectDatePeriod(state);
        },
        handleCalendarClose(state, { payload }) {
            const { inputDates } = state.filters.datePickerRange;
            const { selectDatePeriod } = state.filters.datePickerRange;
            if (selectDatePeriod !== types_1.EDatePeriods.Default) {
                state.filters.datePickerRange.selectDatePeriod = payload;
            }
            else if ((inputDates === null || inputDates === void 0 ? void 0 : inputDates.startDate) || (inputDates === null || inputDates === void 0 ? void 0 : inputDates.endDate)) {
                clearSelectDatePeriod(state);
                const endDateFormatted = (0, dates_1.getOriginalDate)((inputDates === null || inputDates === void 0 ? void 0 : inputDates.endDate) || '');
                const [inputStartDate, inputEndDate] = [
                    (0, dates_1.getOriginalDate)((inputDates === null || inputDates === void 0 ? void 0 : inputDates.startDate) || ''),
                    endDateFormatted && (0, dayjs_1.default)(endDateFormatted).endOf('date').toDate(),
                ];
                state.filters.dates.startDate = inputStartDate;
                state.filters.dates.endDate = inputEndDate;
                if (inputStartDate) {
                    state.filters.datePickerRange.inputDates.startDate =
                        (0, dayjs_1.default)(inputStartDate).format(constants_1.CALENDAR_DATA_FORMAT);
                }
                else {
                    state.filters.datePickerRange.inputDates.startDate =
                        exports.initialState.filters.datePickerRange.inputDates.startDate;
                }
                if (inputEndDate) {
                    state.filters.datePickerRange.inputDates.endDate =
                        (0, dayjs_1.default)(inputEndDate).format(constants_1.CALENDAR_DATA_FORMAT);
                }
                else {
                    state.filters.datePickerRange.inputDates.endDate =
                        exports.initialState.filters.datePickerRange.inputDates.endDate;
                }
                clearBetHistoryPagination(state);
            }
        },
        clearFilterBetsTypes(state) {
            state.filters.betsTypes = null;
        },
        subscribeCashoutSSE() { },
        unsubscribeCashoutSSE(state) {
            state.cashout.data.entries = {};
        },
        setCashoutChannelSSE(state, { payload }) {
            state.cashout.sse.channel = payload;
        },
        setCashoutsSSE(state, { payload }) {
            const { amount } = payload;
            state.cashout.data.entries = {
                ...state.cashout.data.entries,
                [payload.betId]: {
                    ...payload,
                    amount: amount && (0, format_1.adjustAmountForFrontend)(amount),
                },
            };
        },
        setCurrentCashout(state, { payload: { betId } }) {
            state.cashout.fetchItems.error = null;
            state.cashout.data.currentCashout = state.cashout.data.entries[betId];
        },
        unsetCurrentCashout(state) {
            state.cashout.fetchItems.error = null;
            state.cashout.data.currentCashout = null;
            if (!state.cashout.fetchItems.loading) {
                state.cashout.layout.statusView = types_3.ECashoutStatusView.NONE;
            }
        },
        makeCashout(state) {
            state.cashout.fetchItems.loading = true;
        },
        makeCashoutSuccess(state, { payload }) {
            state.cashout.layout.statusView = types_3.ECashoutStatusView.SUCCESS;
            state.cashout.data.soldBet = payload;
            const { betId } = payload;
            const soldBetData = state.bets.entries[betId];
            const soldCashoutData = state.cashout.data.entries[betId];
            if (soldBetData && soldCashoutData) {
                state.bets.entries[betId] = {
                    ...state.bets.entries[betId],
                    status: betweb_openapi_axios_1.BetStatus.Sold,
                    amount: (0, format_1.adjustAmountForBackend)(soldCashoutData.amount),
                };
            }
            const currentCashoutBetId = (state.cashout.data.currentCashout &&
                state.cashout.data.currentCashout.betId) ||
                betId;
            if (currentCashoutBetId) {
                delete state.cashout.data.entries[currentCashoutBetId];
            }
            state.cashout.data.currentCashout = null;
            state.cashout.fetchItems.loading = false;
        },
        makeCashoutError(state, { payload }) {
            state.cashout.fetchItems.error = payload;
            state.cashout.layout.statusView = types_3.ECashoutStatusView.ERROR;
            state.cashout.fetchItems.loading = false;
        },
        setCashoutStatusView(state, { payload }) {
            if (payload === types_3.ECashoutStatusView.NONE) {
                state.cashout.data.soldBet = null;
            }
            state.cashout.layout.statusView = payload;
        },
    },
});
exports.betsHistoryProviderActions = betsHistoryProviderSlice.actions, exports.reducer = betsHistoryProviderSlice.reducer, exports.sliceKey = betsHistoryProviderSlice.name;
const clearBetHistoryPagination = (state) => {
    state.pagination = exports.initialState.pagination;
    state.data.bets.sortedIds = [];
};
const clearBetHistoryAfterDeleteBetsSuccess = (state) => {
    state.bets.fetchItems.loading = true;
    state.bets.deleteItems.loading = false;
    state.bets.deleteItems.error = null;
    state.filters.dates = exports.initialState.filters.dates;
    state.filters.datePickerRange = exports.initialState.filters.datePickerRange;
    state.filters.betsTypes = exports.initialState.filters.betsTypes;
    state.layout.selectedDatePeriod = exports.initialState.layout.selectedDatePeriod;
    state.pagination = exports.initialState.pagination;
    state.data.bets.sortedIds = [];
};
const clearSelectDatePeriod = (state) => {
    state.filters.datePickerRange.selectDatePeriod =
        exports.initialState.filters.datePickerRange.selectDatePeriod;
};
//# sourceMappingURL=slice.js.map